<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
    <div class="max-w-md w-full">
      <!-- Logo -->
      <div class="text-center mb-8">
        <img 
          src="/images/logo.png" 
          alt="Saskatoon Auto Connection" 
          class="h-16 w-auto mx-auto mb-4"
          @error="handleImageError"
        />
        <h1 class="text-2xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
        <p class="text-gray-600">Sign in to access analytics and management tools</p>
      </div>

      <!-- Login Card -->
      <div class="bg-white rounded-2xl shadow-lg p-8">
        <div class="text-center">
          <h2 class="text-xl font-semibold text-gray-900 mb-6">
            Administrator Access
          </h2>
          
          <div v-if="!isLoading" class="space-y-4">
            <form @submit.prevent="handlePasswordLogin" class="space-y-4">
              <div>
                <label for="password" class="sr-only">Admin Password</label>
                <input
                  id="password"
                  v-model="password"
                  type="password"
                  placeholder="Enter admin password"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <button
                type="submit"
                class="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
                Sign In
              </button>
            </form>
            
            <p class="text-xs text-gray-500">
              Only authorized administrators can access this dashboard
            </p>
          </div>
          
          <div v-else class="flex items-center justify-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span class="ml-3 text-gray-600">Signing in...</span>
          </div>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="text-center mt-6 text-xs text-gray-500">
        <p>© 2025 Saskatoon Auto Connection</p>
        <p>Secure admin access with password authentication</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'

const router = useRouter()
const { passwordLogin, isLoading, isAuthenticated, isAdmin } = useAuth()
const password = ref('')

const handlePasswordLogin = async () => {
  try {
    await passwordLogin(password.value)
    password.value = '' // Clear password after attempt
  } catch (error) {
    console.error('Sign-in failed:', error)
    alert('Invalid password. Please try again.')
    password.value = ''
  }
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  const container = img.parentElement
  if (container) {
    container.innerHTML = `
      <div class="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
        <span class="text-white text-xl font-bold">SAC</span>
      </div>
    `
  }
}

// Watch for successful authentication
watch([isAuthenticated, isAdmin], ([authenticated, admin]) => {
  if (authenticated && admin) {
    router.push('/admin/dashboard')
  }
})

onMounted(() => {
  // Check if user is already authenticated
  if (isAuthenticated.value && isAdmin.value) {
    router.push('/admin/dashboard')
  }
})
</script>